import type { HumanPose } from "./CharacterSprites";

/**
 * Kenney Toon Characters (CC0) — park standing humans.
 * Four body archetypes × pose sheets, with soft wardrobe/skin recolors so
 * NPCs read as differentiated without a modular part kit.
 *
 * Sit / eat / frisbee stay on hand-painted canvas (no sit sheet; kids stay small).
 */

const TOON_ROOT = "/assets/kenney-toon";

export type ToonArchetype =
  | "malePerson"
  | "femalePerson"
  | "maleAdventurer"
  | "femaleAdventurer";

export const TOON_ARCHETYPES: readonly ToonArchetype[] = [
  "malePerson",
  "femalePerson",
  "maleAdventurer",
  "femaleAdventurer",
] as const;

/** Kenney pose filenames used by the park. */
type ToonFrame =
  | "idle"
  | "hold"
  | "talk"
  | "side"
  | "duck"
  | "cheer0"
  | "interact"
  | "walk0"
  | "walk2"
  | "walk4"
  | "walk6";

const ALL_FRAMES: readonly ToonFrame[] = [
  "idle",
  "hold",
  "talk",
  "side",
  "duck",
  "cheer0",
  "interact",
  "walk0",
  "walk2",
  "walk4",
  "walk6",
];

/** Original clothing/skin seeds per archetype (approx. Kenney palette). */
interface PaletteSeeds {
  skin: Rgb;
  hair: Rgb;
  shirt: Rgb;
  pants: Rgb;
  shoes: Rgb;
  /** Optional second clothing tone (vest, headband, straps). */
  accent?: Rgb;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

interface Wardrobe {
  skin: Rgb;
  hair: Rgb;
  shirt: Rgb;
  pants: Rgb;
  shoes: Rgb;
  accent: Rgb;
}

const SEEDS: Record<ToonArchetype, PaletteSeeds> = {
  malePerson: {
    skin: { r: 165, g: 106, b: 62 },
    hair: { r: 44, g: 44, b: 44 },
    shirt: { r: 232, g: 144, b: 58 },
    pants: { r: 74, g: 107, b: 200 },
    shoes: { r: 235, g: 235, b: 235 },
    accent: { r: 245, g: 245, b: 245 },
  },
  femalePerson: {
    skin: { r: 212, g: 165, b: 116 },
    hair: { r: 42, g: 42, b: 42 },
    shirt: { r: 76, g: 175, b: 106 },
    pants: { r: 139, g: 107, b: 176 },
    shoes: { r: 232, g: 232, b: 232 },
    accent: { r: 126, g: 200, b: 232 },
  },
  maleAdventurer: {
    skin: { r: 240, g: 200, b: 160 },
    hair: { r: 208, g: 112, b: 48 },
    shirt: { r: 74, g: 154, b: 90 },
    pants: { r: 208, g: 136, b: 64 },
    shoes: { r: 93, g: 64, b: 55 },
    accent: { r: 109, g: 76, b: 65 },
  },
  femaleAdventurer: {
    skin: { r: 245, g: 208, b: 176 },
    hair: { r: 109, g: 76, b: 65 },
    shirt: { r: 100, g: 181, b: 246 },
    pants: { r: 212, g: 165, b: 116 },
    shoes: { r: 109, g: 76, b: 65 },
    accent: { r: 144, g: 202, b: 249 },
  },
};

const SKIN_TONES: readonly Rgb[] = [
  hex("#ffe0b2"),
  hex("#f5d0b0"),
  hex("#e8c4a0"),
  hex("#d4a574"),
  hex("#c68642"),
  hex("#b87333"),
  hex("#8d5524"),
  hex("#6b3f24"),
  hex("#c6865a"),
  hex("#a06b3f"),
  hex("#d2a679"),
  hex("#bc8f6f"),
];

const HAIR_TONES: readonly Rgb[] = [
  hex("#1a1a1a"),
  hex("#3e2723"),
  hex("#5d4037"),
  hex("#f9a825"),
  hex("#bf360c"),
  hex("#4e342e"),
  hex("#0d0d0d"),
  hex("#6d4c41"),
];

const SHIRT_TONES: readonly Rgb[] = [
  hex("#8fb8c9"),
  hex("#d4a090"),
  hex("#7faf9e"),
  hex("#d8c48a"),
  hex("#b59bc4"),
  hex("#c99494"),
  hex("#8fad86"),
  hex("#7fadb8"),
  hex("#d08040"),
  hex("#4a8ab8"),
  hex("#c45a52"),
  hex("#3d7ab0"),
];

const PANT_TONES: readonly Rgb[] = [
  hex("#6d7d88"),
  hex("#8a7468"),
  hex("#5a4038"),
  hex("#4a5860"),
  hex("#3d5a4a"),
  hex("#3d6a9a"),
  hex("#2a2a2e"),
  hex("#9a8a7e"),
];

const SHOE_TONES: readonly Rgb[] = [
  hex("#5d4037"),
  hex("#3e464c"),
  hex("#2a2420"),
  hex("#efebe9"),
  hex("#6d4c41"),
];

const ACCENT_TONES: readonly Rgb[] = [
  hex("#fff6e4"),
  hex("#90caf9"),
  hex("#f6e6ec"),
  hex("#e4f0f2"),
  hex("#ffe0b2"),
  hex("#c99494"),
];

let ready = false;
let loadPromise: Promise<void> | null = null;
const images = new Map<string, HTMLImageElement>();

export function toonCharactersReady(): boolean {
  return ready;
}

export function loadToonCharacterAssets(): Promise<void> {
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = (async () => {
    try {
      const jobs: Promise<void>[] = [];
      for (const arch of TOON_ARCHETYPES) {
        for (const frame of ALL_FRAMES) {
          jobs.push(
            loadImage(`${TOON_ROOT}/${arch}/${frame}.png`).then((img) => {
              if (img) {
                images.set(`${arch}/${frame}`, img);
              }
            }),
          );
        }
      }
      await Promise.all(jobs);
      const count = images.size;
      ready = count >= TOON_ARCHETYPES.length * 4;
      if (ready) {
        console.info(`[kenney-toon] loaded ${count} character frames`);
      } else {
        console.info(`[kenney-toon] incomplete (${count}); canvas humans stay in use`);
      }
    } catch (err) {
      console.warn("[kenney-toon] load failed; using canvas humans", err);
      ready = false;
    }
  })();
  return loadPromise;
}

/**
 * Build a recolored + prop-decorated toon canvas for a park pose, or null to
 * fall back to hand-painted humans.
 */
export function paintToonHuman(pose: HumanPose, variant: number): HTMLCanvasElement | null {
  if (!ready) {
    return null;
  }
  if (pose === "chat" || pose.startsWith("frisbee")) {
    return null;
  }
  // sit / eat use Kenney crouch (duck); standing poses use idle/hold/etc.
  const arch = TOON_ARCHETYPES[variant % TOON_ARCHETYPES.length]!;
  const frame = poseToFrame(pose);
  const img = images.get(`${arch}/${frame}`);
  if (!img) {
    return null;
  }

  const wardrobe = wardrobeFor(variant);
  const seeds = SEEDS[arch];
  const base = recolorImage(img, seeds, wardrobe);
  paintPropOverlay(base, pose, wardrobe.shirt);
  return base;
}

/** Kenney walk cycle frame (0..3) for ambient leash walkers. */
export function paintToonWalk(variant: number, step: number): HTMLCanvasElement | null {
  if (!ready) {
    return null;
  }
  const arch = TOON_ARCHETYPES[variant % TOON_ARCHETYPES.length]!;
  const frames: ToonFrame[] = ["walk0", "walk2", "walk4", "walk6"];
  const frame = frames[((step % 4) + 4) % 4]!;
  const img = images.get(`${arch}/${frame}`);
  if (!img) {
    return null;
  }
  const wardrobe = wardrobeFor(variant);
  return recolorImage(img, SEEDS[arch], wardrobe);
}

export function toonArchetypeFor(variant: number): ToonArchetype {
  return TOON_ARCHETYPES[variant % TOON_ARCHETYPES.length]!;
}

function poseToFrame(pose: HumanPose): ToonFrame {
  switch (pose) {
    case "sit":
    case "eat":
    case "drumReady":
      return "duck";
    case "stand":
    case "cook":
      return "idle";
    case "chat":
      return "talk";
    case "fishReady":
    case "tennisReady":
    case "basketReady":
      return "hold";
    case "fishCast":
    case "tennisSwing":
    case "basketShoot":
      return "interact";
    case "drumHit":
      return "cheer0";
    default:
      return "idle";
  }
}

function wardrobeFor(variant: number): Wardrobe {
  return {
    skin: SKIN_TONES[variant % SKIN_TONES.length]!,
    hair: HAIR_TONES[(variant * 3 + 1) % HAIR_TONES.length]!,
    shirt: SHIRT_TONES[(variant * 5 + 2) % SHIRT_TONES.length]!,
    pants: PANT_TONES[(variant * 7 + 3) % PANT_TONES.length]!,
    shoes: SHOE_TONES[(variant * 11 + 4) % SHOE_TONES.length]!,
    accent: ACCENT_TONES[(variant * 13 + 5) % ACCENT_TONES.length]!,
  };
}

function recolorImage(
  img: HTMLImageElement,
  seeds: PaletteSeeds,
  wardrobe: Wardrobe,
): HTMLCanvasElement {
  const scale = 2;
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const src = document.createElement("canvas");
  src.width = w;
  src.height = h;
  const sg = src.getContext("2d")!;
  sg.imageSmoothingEnabled = false;
  sg.drawImage(img, 0, 0);
  const data = sg.getImageData(0, 0, w, h);
  const px = data.data;

  const targets: { seed: Rgb; next: Rgb; maxDist: number }[] = [
    { seed: seeds.skin, next: wardrobe.skin, maxDist: 72 },
    { seed: seeds.hair, next: wardrobe.hair, maxDist: 55 },
    { seed: seeds.shirt, next: wardrobe.shirt, maxDist: 70 },
    { seed: seeds.pants, next: wardrobe.pants, maxDist: 70 },
    { seed: seeds.shoes, next: wardrobe.shoes, maxDist: 42 },
  ];
  // Only remap accent when it isn't a near-white undershirt — otherwise eye
  // sclera gets painted into cream rectangles over every face.
  if (seeds.accent && !isNearWhite(seeds.accent)) {
    targets.push({ seed: seeds.accent, next: wardrobe.accent, maxDist: 50 });
  }

  for (let i = 0; i < px.length; i += 4) {
    const a = px[i + 3]!;
    const r = px[i]!;
    const g = px[i + 1]!;
    const b = px[i + 2]!;
    // Drop opaque black backdrops some exports leave behind.
    if (a < 12 || r + g + b < 28) {
      px[i + 3] = 0;
      continue;
    }
    // Keep eyes / teeth / highlights (Kenney sclera is off-pure-white).
    if (isNearWhite({ r, g, b })) {
      continue;
    }
    // Keep near-black pupils / outlines / brows / mustaches.
    if (r < 45 && g < 45 && b < 45) {
      continue;
    }

    let best = -1;
    let bestDist = Infinity;
    for (let t = 0; t < targets.length; t++) {
      const d = colorDist(r, g, b, targets[t]!.seed);
      if (d < bestDist && d <= targets[t]!.maxDist) {
        bestDist = d;
        best = t;
      }
    }
    if (best < 0) {
      continue;
    }
    const from = targets[best]!.seed;
    const to = targets[best]!.next;
    const lum = (r + g + b) / (from.r + from.g + from.b + 1);
    px[i] = clamp(to.r * lum);
    px[i + 1] = clamp(to.g * lum);
    px[i + 2] = clamp(to.b * lum);
  }
  sg.putImageData(data, 0, 0);

  const out = document.createElement("canvas");
  out.width = w * scale;
  out.height = h * scale;
  const g = out.getContext("2d")!;
  g.imageSmoothingEnabled = false;
  g.drawImage(src, 0, 0, out.width, out.height);
  return out;
}

/** Activity props painted on top of the Kenney body. */
function paintPropOverlay(
  canvas: HTMLCanvasElement,
  pose: HumanPose,
  shirt: Rgb,
): void {
  const g = canvas.getContext("2d")!;
  g.imageSmoothingEnabled = false;
  const w = canvas.width;
  const h = canvas.height;
  // Art is ~96×128 at ×2 → work in local 0..96 / 0..128 space then scale.
  const s = w / 96;

  if (pose === "fishReady" || pose === "fishCast") {
    const rod = "#6d4c41";
    const reel = "#78909c";
    g.fillStyle = rod;
    if (pose === "fishCast") {
      fillRoundLocal(g, s, 52, 8, 4, 36, rod);
      fillOvalLocal(g, s, 50, 28, 8, 6, reel);
      g.strokeStyle = "rgba(144,164,174,0.75)";
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(54 * s, 8 * s);
      g.lineTo(88 * s, 40 * s);
      g.stroke();
    } else {
      fillRoundLocal(g, s, 54, 36, 4, 28, rod);
      fillOvalLocal(g, s, 52, 52, 8, 6, reel);
      fillRoundLocal(g, s, 56, 22, 3, 18, rod);
      g.strokeStyle = "rgba(144,164,174,0.7)";
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(58 * s, 22 * s);
      g.lineTo(90 * s, 52 * s);
      g.stroke();
    }
  } else if (pose === "tennisReady" || pose === "tennisSwing") {
    g.fillStyle = "#8d6e63";
    if (pose === "tennisSwing") {
      fillRoundLocal(g, s, 58, 10, 4, 18, "#8d6e63");
      fillOvalLocal(g, s, 52, 0, 18, 16, "#a8c878");
      g.strokeStyle = "#5a7a40";
      g.lineWidth = 2;
      g.strokeRect(54 * s, 2 * s, 14 * s, 12 * s);
    } else {
      fillRoundLocal(g, s, 58, 56, 4, 14, "#8d6e63");
      fillOvalLocal(g, s, 52, 66, 16, 16, "#a8c878");
      g.strokeStyle = "#5a7a40";
      g.lineWidth = 2;
      g.strokeRect(54 * s, 68 * s, 12 * s, 12 * s);
    }
  } else if (pose === "eat") {
    fillOvalLocal(g, s, 28, 70, 22, 14, "#fff8e1");
    fillOvalLocal(g, s, 34, 72, 10, 8, "#d46a5a");
  } else if (pose === "cook") {
    const apron = "#fff6e4";
    fillRoundLocal(g, s, 34, 58, 28, 30, apron);
    fillRoundLocal(g, s, 62, 40, 4, 22, "#78909c");
    fillRoundLocal(g, s, 58, 34, 12, 6, "#90a4ae");
  } else if (pose === "drumReady" || pose === "drumHit") {
    const wood = "#8d6d3a";
    const skin = "#ffe0b2";
    const dy = pose === "drumHit" ? 70 : 78;
    fillOvalLocal(g, s, 30, dy, 36, 28, wood);
    fillOvalLocal(g, s, 34, dy - 8, 28, 22, skin);
  } else if (pose === "chat") {
    const cup = rgbCss(shirt);
    fillRoundLocal(g, s, 62, 70, 10, 16, cup);
    fillOvalLocal(g, s, 62, 66, 10, 8, "#fff6e0");
  }
}

function fillRoundLocal(
  g: CanvasRenderingContext2D,
  s: number,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
): void {
  g.fillStyle = color;
  const r = Math.min(w, h) * 0.2 * s;
  const X = x * s;
  const Y = y * s;
  const W = w * s;
  const H = h * s;
  g.beginPath();
  g.moveTo(X + r, Y);
  g.arcTo(X + W, Y, X + W, Y + H, r);
  g.arcTo(X + W, Y + H, X, Y + H, r);
  g.arcTo(X, Y + H, X, Y, r);
  g.arcTo(X, Y, X + W, Y, r);
  g.closePath();
  g.fill();
}

function fillOvalLocal(
  g: CanvasRenderingContext2D,
  s: number,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
): void {
  g.fillStyle = color;
  g.beginPath();
  g.ellipse((x + w / 2) * s, (y + h / 2) * s, (w / 2) * s, (h / 2) * s, 0, 0, Math.PI * 2);
  g.fill();
}

function colorDist(r: number, g: number, b: number, seed: Rgb): number {
  const dr = r - seed.r;
  const dg = g - seed.g;
  const db = b - seed.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

/** Eyes / teeth / shoe highlights — leave alone during wardrobe remap. */
function isNearWhite(c: Rgb): boolean {
  return (
    c.r > 200 &&
    c.g > 195 &&
    c.b > 185 &&
    Math.abs(c.r - c.g) < 28 &&
    Math.abs(c.g - c.b) < 32
  );
}

function clamp(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function hex(css: string): Rgb {
  const h = css.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbCss(c: Rgb): string {
  return `rgb(${c.r},${c.g},${c.b})`;
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}
